import React, { useState, useEffect, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Container, Row, Col, Button, Modal } from "react-bootstrap";
import { ArrowLeft, ArrowRight, X } from "react-bootstrap-icons";
import { clearActiveExercise } from "../../../redux/slices/exerciseSlice";
import { UserContext } from "../../user/UserContext";
import { fetchUserVocabulary } from "../../../redux/slices/userVocabularySlice";
import "./Sprint.css";

export default function Sprint() {
  const dispatch = useDispatch(); // Initialize the Redux dispatch function
  const { userAttributes } = useContext(UserContext); // Access user attributes from context
  const { userWords, loading } = useSelector((state) => state.userVocabulary); // Access relevant state from the Redux store

  const [exerciseTimer, setExerciseTimer] = useState(60); // State to track the timer for the exercise
  const [countdownTimer, setCountdownTimer] = useState(4); // State to track the countdown before the exercise starts
  const [isCountingDown, setIsCountingDown] = useState(true); // State to manage the countdown state
  const [exerciseFinished, setExerciseFinished] = useState(false); // State to check if the exercise has finished
  const [correctAnswers, setCorrectAnswers] = useState(0); // State to track the number of correct answers
  const [words, setWords] = useState([...userWords]); // Clone userWords into a local state for this exercise
  const [currentWordIndex, setCurrentWordIndex] = useState(0); // Track the current index of the word being displayed
  const [isCorrectTranslation, setIsCorrectTranslation] = useState(null); // State to check if the shown translation is correct
  const [currentTranslation, setCurrentTranslation] = useState(""); // State to store the current translation being displayed

  // Fetch user vocabulary if it hasn't been loaded yet
  useEffect(() => {
    if (userWords.length === 0 && !loading) {
      const loadData = async () => {
        try {
          await dispatch(fetchUserVocabulary({ owner: userAttributes.sub }));
        } catch (error) {
          console.error("Error in loadData:", error);
        }
      };
      loadData(); // Trigger the async function to load data
    }
  }, [dispatch, userAttributes.sub, userWords.length, loading]);

  // Countdown before the exercise starts
  useEffect(() => {
    let countdownInterval;

    if (countdownTimer > 0) {
      countdownInterval = setInterval(() => {
        setCountdownTimer((prev) => prev - 1);
      }, 1000); // Decrease the countdown timer every second
    } else if (countdownTimer === 0) {
      setIsCountingDown(false); // Stop the countdown
      clearInterval(countdownInterval);
    }

    return () => {
      clearInterval(countdownInterval);
    };
  }, [countdownTimer]);

  // Exercise timer management
  useEffect(() => {
    let timerInterval;

    if (exerciseTimer > 0 && !exerciseFinished && !isCountingDown) {
      timerInterval = setInterval(() => {
        setExerciseTimer((prevTimer) => prevTimer - 1);
      }, 1000); // Decrease the exercise timer every second
    } else if (exerciseTimer === 0 && !exerciseFinished) {
      setExerciseFinished(true); // End the exercise when the timer reaches 0
      clearInterval(timerInterval);
    }

    return () => {
      clearInterval(timerInterval);
    };
  }, [exerciseTimer, exerciseFinished, isCountingDown]);

  // Handle the word and translation selection logic
  useEffect(() => {
    if (!isCountingDown && currentWordIndex < words.length) {
      const currentWord = words[currentWordIndex];

      // Set the chance of showing the correct translation to 50%
      const showCorrectTranslation = Math.random() < 0.5;

      let translation;
      if (showCorrectTranslation) {
        translation = currentWord.translation; // Show the correct translation
        setIsCorrectTranslation(true); // Mark the translation as correct
      } else {
        // Filter out the correct translation from the list of possible incorrect translations
        const possibleIncorrectTranslations = userWords
          .filter((word) => word.translation !== currentWord.translation)
          .map((word) => word.translation);

        // Select a random incorrect translation
        const randomIncorrectTranslation =
          possibleIncorrectTranslations[
            Math.floor(Math.random() * possibleIncorrectTranslations.length)
          ];

        translation = randomIncorrectTranslation; // Show an incorrect translation
        setIsCorrectTranslation(false); // Mark the translation as incorrect
      }

      setCurrentTranslation(translation); // Update the translation state
    } else if (currentWordIndex >= words.length) {
      setExerciseFinished(true); // End the exercise when all words are used
    }
  }, [currentWordIndex, words, userWords, isCountingDown]);

  // Handle the user's answer
  function handleAnswer(isCorrect) {
    if (isCorrect === isCorrectTranslation) {
      setCorrectAnswers((prev) => {
        const newCount = prev + 1;
        return newCount;
      });
    }

    setCurrentWordIndex((prevIndex) => prevIndex + 1); // Move to the next word
  }

  // Key event listener for arrow keys
  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "ArrowLeft") {
        handleAnswer(false); // Trigger incorrect answer logic
      } else if (event.key === "ArrowRight") {
        handleAnswer(true); // Trigger correct answer logic
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCorrectTranslation]);

  // Restart the exercise
  function handleTryAgain() {
    setExerciseFinished(false); // Hide the finished modal
    setCorrectAnswers(0); // Reset the correct answers count
    setExerciseTimer(60); // Reset the exercise timer
    setWords([...userWords]); // Reset the words for the new exercise
    setCurrentWordIndex(0); // Start from the first word again
    setCountdownTimer(4); // Reset the countdown timer
    setIsCountingDown(true); // Start the countdown
  }

  return (
    <Container className="d-flex justify-content-center align-items-center vh-100">
      {isCountingDown ? (
        <div className="text-center">
          <h1 className="display-1">{countdownTimer}</h1>
          <p>Get Ready</p>
        </div>
      ) : (
        <div className="vocabulary-card-container text-center p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="score">{correctAnswers}</h2>
            <h2 className="timer">{exerciseTimer}</h2>
            <div className="d-flex align-items-center">
              {/* <MusicNote size={24} /> */}
              <button
                className="btn btn-link ms-2"
                style={{
                  padding: "0",
                  fontSize: "1.5rem",
                  lineHeight: "1",
                  border: "none",
                  background: "none",
                }}
                onClick={() => dispatch(clearActiveExercise())}
              >
                <X size={24} />
              </button>
            </div>
          </div>

          <div className="vocabulary-card">
            <div className="progress-indicator d-flex justify-content-center mb-3">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>

            <div className="text-center mb-3">
              <h1 className="word">{words[currentWordIndex]?.word}</h1>
              <p className="translation">{currentTranslation}</p>
            </div>

            <Row className="justify-content-center mb-3">
              <Col xs={12} md={6} className="mb-2">
                <Button
                  variant="danger"
                  size="lg"
                  className="w-100"
                  onClick={() => handleAnswer(false)}
                >
                  Wrong
                </Button>
              </Col>
              <Col xs={12} md={6}>
                <Button
                  variant="success"
                  size="lg"
                  className="w-100"
                  onClick={() => handleAnswer(true)}
                >
                  Correct
                </Button>
              </Col>
            </Row>

            <div className="d-flex justify-content-between">
              <ArrowLeft size={32} />
              <ArrowRight size={32} />
            </div>
          </div>
        </div>
      )}

      <Modal
        show={exerciseFinished}
        onHide={() => setExerciseFinished(false)}
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Exercise Finished</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          The exercise has finished. You answered {correctAnswers} correctly.
          Would you like to try again or go back to Exercises?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => dispatch(clearActiveExercise())}
          >
            Back to Exercises
          </Button>
          <Button variant="primary" onClick={handleTryAgain}>
            Try Again
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
